import type { BlockKind, ComplexFormat, FormatKind } from './types';

/* Формат комплекса (CON-18) и тип блока.
   ВНИМАНИЕ: \w и \b в JS не видят кириллицу — окончания ловим явным [а-яё]*. */

export interface ParsedFormat extends ComplexFormat {
  /** Что именно совпало — по нему формат вырезается из названия */
  src: string;
}

export function parseFmt(s: string | null | undefined): ParsedFormat | null {
  if (!s) return null;
  const t = s.trim();
  let m: RegExpMatchArray | null;
  if ((m = t.match(/^EMOM\s*(\d+)/i))) return { src: m[0], k: 'EMOM', rounds: +m[1], work: 60, rest: 0, total: +m[1] * 60 };
  if ((m = t.match(/^E(\d+)MOM\s*(\d+)/i)))
    return { src: m[0], k: 'EMOM', rounds: +m[2], work: +m[1] * 60, rest: 0, total: +m[2] * +m[1] * 60 };
  if ((m = t.match(/^AMRAP\s*(\d+)/i))) return { src: m[0], k: 'AMRAP', rounds: 1, work: +m[1] * 60, rest: 0, total: +m[1] * 60 };
  if ((m = t.match(/^(?:TABATA|табат[а-яё]*)/i))) return { src: m[0], k: 'TABATA', rounds: 8, work: 20, rest: 10, total: 240 };
  if ((m = t.match(/^(\d+)\s*(?:RFT|раунд[а-яё]*\s*на\s*время|rounds?\s*for\s*time)\s*(\d+)?/i)))
    return { src: m[0], k: 'FOR_TIME', rounds: +m[1], work: 0, rest: 0, total: m[2] ? +m[2] * 60 : 0 };
  if ((m = t.match(/^FOR\s*TIME\s*(\d+)?/i)))
    return { src: m[0], k: 'FOR_TIME', rounds: 1, work: 0, rest: 0, total: m[1] ? +m[1] * 60 : 0 };
  if ((m = t.match(/^(?:death\s*by|дез\s*бай)\s*(\d+)?/i)))
    return { src: m[0], k: 'DEATH_BY', rounds: 0, work: (+m[1] || 1) * 60, rest: 0, total: 0, start: 1 };
  if ((m = t.match(/^(?:not\s*for\s*time|не\s*на\s*время|NFT)/i))) return { src: m[0], k: 'NFT', rounds: 1, work: 0, rest: 0, total: 0 };
  if ((m = t.match(/(\d+)\s*(?:rounds?|раунд[а-яё]*)\D+(\d+)\s*(?:sec|сек)\D+(\d+)\s*(?:sec|сек)/i)))
    return { src: m[0], k: 'INTERVALS', rounds: +m[1], work: +m[2], rest: +m[3], total: +m[1] * (+m[2] + +m[3]) };
  if ((m = t.match(/кажд[а-яё]*\s*(\d+)\s*(сек|мин)[а-яё]*\D+(\d+)\s*(?:раунд|круг|повтор)[а-яё]*/i))) {
    const w = m[2].toLowerCase() === 'мин' ? +m[1] * 60 : +m[1];
    return { src: m[0], k: 'EMOM', rounds: +m[3], work: w, rest: 0, total: +m[3] * w };
  }
  if ((m = t.match(/(\d+)\s*(?:раунд|круг)[а-яё]*\s*по\s*(\d+)\s*(мин|сек)[а-яё]*(?:[^\d]*отдых\D*?(\d+)\s*(мин|сек)[а-яё]*)?/i))) {
    const sec = (v: string, u?: string) => (u && u.toLowerCase() === 'мин' ? +v * 60 : +v);
    const w = sec(m[2], m[3]);
    const r = m[4] ? sec(m[4], m[5]) : 0;
    return { src: m[0], k: r ? 'INTERVALS' : 'ROUNDS', rounds: +m[1], work: w, rest: r, total: +m[1] * (w + r) };
  }
  if ((m = t.match(/^на\s*время\s*(\d+)?/i))) return { src: m[0], k: 'FOR_TIME', rounds: 1, work: 0, rest: 0, total: m[1] ? +m[1] * 60 : 0 };
  return null;
}

export const plural3 = (n: number, a: string, b: string, c: string) => {
  const m = n % 10;
  const h = n % 100;
  return h >= 11 && h <= 14 ? c : m === 1 ? a : m >= 2 && m <= 4 ? b : c;
};
export const mmss = (t: number) => Math.floor(t / 60) + ':' + String(Math.round(t) % 60).padStart(2, '0');
const mmssShort = (s: number) => (s % 60 ? mmss(s) : String(s / 60));

/** Короткая подпись формата — чип в шапке блока. */
export function fmtLabel(f: ComplexFormat | null | undefined): string {
  if (!f) return '';
  switch (f.k) {
    case 'AMRAP':
      return 'AMRAP ' + mmssShort(f.total);
    case 'EMOM':
      return (f.work === 60 ? 'EMOM ' : 'E' + mmssShort(f.work) + 'MOM ') + f.rounds;
    case 'FOR_TIME':
      return (
        (f.rounds > 1 ? f.rounds + ' ' + plural3(f.rounds, 'раунд', 'раунда', 'раундов') + ' на время' : 'На время') +
        (f.total ? ' · до ' + mmssShort(f.total) + ' мин' : '')
      );
    case 'TABATA':
      return `Табата ${f.rounds} × ${f.work}/${f.rest}`;
    case 'INTERVALS':
      return `${f.rounds} × ${mmss(f.work)} / ${mmss(f.rest)}`;
    case 'ROUNDS':
      return `${f.rounds} × ${mmss(f.work)}`;
    case 'DEATH_BY':
      return 'Death by · ' + mmss(f.work);
    case 'NFT':
      return 'Не на время';
  }
}

/** Расшифровка формата человеческим языком — что увидит клиент. */
export function fmtDesc(f: ComplexFormat | null | undefined): string {
  if (!f) return '';
  switch (f.k) {
    case 'AMRAP':
      return 'максимум раундов за ' + mmss(f.total);
    case 'EMOM':
      return 'каждые ' + mmss(f.work) + ' новый подход, ' + f.rounds + ' раз · всего ' + mmss(f.total);
    case 'FOR_TIME':
      return (
        (f.rounds > 1 ? f.rounds + ' ' + plural3(f.rounds, 'раунд', 'раунда', 'раундов') + ' как можно быстрее' : 'как можно быстрее') +
        (f.total ? ', лимит ' + mmss(f.total) : '')
      );
    case 'TABATA':
    case 'INTERVALS':
      return f.rounds + ' × ' + mmss(f.work) + ' работы через ' + mmss(f.rest) + ' отдыха';
    case 'ROUNDS':
      return f.rounds + ' × ' + mmss(f.work);
    case 'DEATH_BY':
      return 'каждые ' + mmss(f.work) + ' на один повтор больше — до отказа';
    case 'NFT':
      return 'без таймера, на качество';
  }
}

export const FMT_TYPES: { k: FormatKind; n: string; d: Omit<ComplexFormat, 'k'> }[] = [
  { k: 'AMRAP', n: 'AMRAP', d: { rounds: 1, work: 900, rest: 0, total: 900 } },
  { k: 'EMOM', n: 'EMOM', d: { rounds: 12, work: 60, rest: 0, total: 720 } },
  { k: 'FOR_TIME', n: 'На время', d: { rounds: 1, work: 0, rest: 0, total: 0 } },
  { k: 'TABATA', n: 'Табата', d: { rounds: 8, work: 20, rest: 10, total: 240 } },
  { k: 'INTERVALS', n: 'Интервалы', d: { rounds: 4, work: 180, rest: 60, total: 960 } },
  { k: 'DEATH_BY', n: 'Death by', d: { rounds: 0, work: 60, rest: 0, total: 0, start: 1 } },
  { k: 'NFT', n: 'Не на время', d: { rounds: 1, work: 0, rest: 0, total: 0 } },
];

export const BLOCK_TYPES: { k: BlockKind | null; n: string }[] = [
  { k: null, n: 'без типа' },
  { k: 'warmup', n: 'Разминка' },
  { k: 'strength', n: 'Силовая' },
  { k: 'complex', n: 'Комплекс' },
  { k: 'cooldown', n: 'Заминка' },
  { k: 'stretch', n: 'Растяжка' },
  { k: 'accessory', n: 'Подкачка' },
  { k: 'gymnastics', n: 'Гимнастика' },
];
export const typeName = (k: BlockKind | null) => (BLOCK_TYPES.find((t) => t.k === (k || null)) || BLOCK_TYPES[0]).n;

/** Папка базы блоков по типу (TPL-1) */
export const TPL_FOLDERS = ['Разминки', 'Силовые блоки', 'Комплексы', 'Заминки'] as const;
export const TYPE_FOLDER: Record<BlockKind, (typeof TPL_FOLDERS)[number]> = {
  warmup: 'Разминки',
  strength: 'Силовые блоки',
  complex: 'Комплексы',
  cooldown: 'Заминки',
  stretch: 'Заминки',
  accessory: 'Силовые блоки',
  gymnastics: 'Силовые блоки',
};

const TYPE_WORDS: [BlockKind, string][] = [
  ['warmup', 'разминк'],
  ['strength', 'силов'],
  ['complex', 'комплекс'],
  ['cooldown', 'заминк'],
  ['stretch', 'растяжк'],
  ['accessory', 'подкачк'],
  ['gymnastics', 'гимнастик'],
];

/** Тип по слову в названии: «Разминка ТА» → warmup. Решает первое слово. */
export function typeOfTitle(title: string | null | undefined): BlockKind | null {
  const t = ' ' + String(title || '').toLowerCase().replace(/ё/g, 'е');
  let best: BlockKind | null = null;
  let at = Infinity;
  for (const [k, w] of TYPE_WORDS) {
    const m = t.match(new RegExp('[^а-я]' + w));
    if (m && m.index! < at) {
      best = k;
      at = m.index!;
    }
  }
  return best;
}

const RE_SCHEME_SEG = /\d+\s*[x×хХ]\s*\d+|\d+(?:\s*-\s*\d+)+/;

/** Найти формат в названии: каждая часть через «·», потом строка целиком. */
export function findFmt(title: string): ParsedFormat | null {
  const t = String(title || '').trim();
  for (const p of t.split(/\s*·\s*/)) {
    const f = parseFmt(p);
    if (f) return f;
  }
  return parseFmt(t);
}

/** Вырезать формат из названия: «EMOM 12 · сила» → «сила». */
export function stripFmt(title: string): string {
  return String(title || '')
    .split(/\s*·\s*/)
    .map((seg) => {
      const f = parseFmt(seg);
      if (!f || RE_SCHEME_SEG.test(seg)) return seg;
      return seg.replace(f.src, '').replace(/^[\s:\-–—,]+|[\s:\-–—,]+$/g, '').trim();
    })
    .filter(Boolean)
    .join(' · ');
}

/** Подпись типа: «Разминка», «Комплекс · AMRAP 15». */
export function blockTypeLabel(b: { kind: BlockKind | null; fmt: ComplexFormat | null }): string {
  if (!b.kind) return '';
  return b.kind === 'complex' && b.fmt ? 'Комплекс · ' + fmtLabel(b.fmt) : typeName(b.kind);
}

/** Рекорды по умолчанию: у каждого клиента сразу стоят пустыми строками (тренер может убрать лишние).
    Список клиента — settings.pmKeys; пока его не меняли, берётся этот. Строки с данными видны всегда. */
export const DEFAULT_PM = ['squat', 'fsquat', 'dead', 'press', 'bench', 'jerk', 'snatch', 'clean'];
export const pmKeysOf = (settings: unknown): string[] => {
  const k = (settings as { pmKeys?: unknown } | null)?.pmKeys;
  return Array.isArray(k) ? k.filter((x): x is string => typeof x === 'string') : DEFAULT_PM;
};
